import React, { useState, useEffect } from "react";
import allImgPaths from "assets/images";
import CustomCheckBox from "components/CustomCheckBox";
import Toast from "../Toast";

interface SearchQueriesModalProps {
    isOpen: boolean;
    onClose: () => void;
    queriesData: {
        product_query: string;
        patent_query: string;
        prior_art_query: string;
        elements_for_profiling: string[];
    } | null;
    isLoading: boolean;
    onRegenerateQueries: (selectedElements: string[]) => void;
    onFindSimilarProducts: (queries: {
        product_query?: string;
        patent_query?: string;
        prior_art_query?: string;
        num_results: number;
    }) => void;
    isRegenerating: boolean;
    isFinding: boolean;
}

const SearchQueriesModal: React.FC<SearchQueriesModalProps> = ({
    isOpen,
    onClose,
    queriesData,
    isLoading,
    onRegenerateQueries,
    onFindSimilarProducts,
    isRegenerating,
    isFinding,
}) => {
    const [productQuery, setProductQuery] = useState("");
    const [patentQuery, setPatentQuery] = useState("");
    const [priorArtQuery, setPriorArtQuery] = useState("");
    const [allElements, setAllElements] = useState<string[]>([]);
    const [selectedElements, setSelectedElements] = useState<string[]>([]);
    const [numResults, setNumResults] = useState(5);
    const [useProductQuery, setUseProductQuery] = useState(true);
    const [usePatentQuery, setUsePatentQuery] = useState(true);
    const [usePriorArtQuery, setUsePriorArtQuery] = useState(true);
    const isInitialLoad = React.useRef(true);

    useEffect(() => {
        if (queriesData) {
            setProductQuery(queriesData.product_query || "");
            setPatentQuery(queriesData.patent_query || "");
            setPriorArtQuery(queriesData.prior_art_query || "");
            if (isInitialLoad.current) {
                const elements = queriesData.elements_for_profiling || [];
                setAllElements(elements);
                setSelectedElements(elements);
                isInitialLoad.current = false;
            }
        }
    }, [queriesData]);

    const resetState = () => {
        setProductQuery("");
        setPatentQuery("");
        setPriorArtQuery("");
        setAllElements([]);
        setSelectedElements([]);
        setNumResults(5);
        setUseProductQuery(true);
        setUsePatentQuery(true);
        setUsePriorArtQuery(true);
        isInitialLoad.current = true;
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleElementToggle = (element: string, checked: boolean) => {
        if (checked) {
            setSelectedElements((prev) => [...prev, element]);
        } else {
            setSelectedElements((prev) => prev.filter((e) => e !== element));
        }
    };

    const handleSelectAllElements = () => {
        if (selectedElements.length === allElements.length) {
            setSelectedElements([]);
        } else {
            setSelectedElements([...allElements]);
        }
    };

    const handleRegenerateQueries = () => {
        if (selectedElements.length === 0) {
            Toast.warning("Warning", "Please select at least one claim element");
            return;
        }
        onRegenerateQueries(selectedElements);
    };

    const handleFindSimilarProducts = () => {
        if (!useProductQuery && !usePatentQuery && !usePriorArtQuery) {
            Toast.warning("Warning", "Please enable at least one query type");
            return;
        }
        onFindSimilarProducts({
            ...(useProductQuery && productQuery.trim() && { product_query: productQuery }),
            ...(usePatentQuery && patentQuery.trim() && { patent_query: patentQuery }),
            ...(usePriorArtQuery && priorArtQuery.trim() && { prior_art_query: priorArtQuery }),
            num_results: numResults,
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[999] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
                <div className="fixed inset-0 bg-black opacity-30"></div>

                <div className="relative bg-white rounded-lg w-full max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="pl-6 pr-6 pt-6">
                        <div className="flex items-center gap-x-2">
                            <img
                                src={allImgPaths.ExaminerCitationsModalIcon}
                                alt=""
                                className="w-10 h-10"
                            />
                            <button
                                onClick={handleClose}
                                className="absolute right-6 top-6 hover:bg-[#f2f4f7] rounded p-1"
                            >
                                <img
                                    src={allImgPaths.closeIcon}
                                    alt="Close"
                                    className="w-3 h-3"
                                />
                            </button>
                        </div>
                    </div>

                    <div className="px-6 pt-4 pb-2">
                        <h2 className="text-base font-interSemiBold text-[#0A090B]">
                            Review Search Queries
                        </h2>
                        <p className="text-sm font-interRegular text-[#4F4D55] mt-1">
                            Review and edit the generated queries before searching. You can also
                            select which claim elements to include.
                        </p>
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto px-6 pb-4">
                        {isLoading ? (
                            <div className="flex flex-col justify-center items-center h-60">
                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#1751D0]"></div>
                                <p className="text-sm text-[#4F4D55] mt-4">
                                    Generating search queries...
                                </p>
                            </div>
                        ) : (
                            <div className="flex gap-6">
                                {/* Left: Queries */}
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-interSemiBold text-[#0A090B] mb-3">
                                        Generated Queries
                                    </h3>

                                    {/* Product Query */}
                                    <div className="mb-4">
                                        <div className="flex items-center justify-between mb-1.5">
                                            <label className="text-sm font-interMedium text-[#2D2B32]">
                                                Product Query
                                            </label>
                                            <label className="flex items-center gap-x-1.5 cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={useProductQuery}
                                                    onChange={(e) => setUseProductQuery(e.target.checked)}
                                                    className="w-3.5 h-3.5 accent-[#3062D4] cursor-pointer"
                                                />
                                                <span className="text-xs font-interRegular text-[#4F4D55]">
                                                    Include in search
                                                </span>
                                            </label>
                                        </div>
                                        <textarea
                                            value={productQuery}
                                            onChange={(e) => setProductQuery(e.target.value)}
                                            disabled={!useProductQuery}
                                            className={`w-full border rounded-lg px-3 py-2.5 text-sm font-interRegular text-[#0A090B] resize-none focus:outline-none focus:border-[#3062D4] focus:ring-1 focus:ring-[#3062D4] transition-opacity ${
                                                useProductQuery
                                                    ? "border-[#E6E6E6]"
                                                    : "border-[#E6E6E6] opacity-40 bg-[#F9FAFB] cursor-not-allowed"
                                            }`}
                                            rows={3}
                                            placeholder="Product search query..."
                                        />
                                    </div>

                                    {/* Patent Query */}
                                    <div className="mb-4">
                                        <div className="flex items-center justify-between mb-1.5">
                                            <label className="text-sm font-interMedium text-[#2D2B32]">
                                                Patent Query
                                            </label>
                                            <label className="flex items-center gap-x-1.5 cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={usePatentQuery}
                                                    onChange={(e) => setUsePatentQuery(e.target.checked)}
                                                    className="w-3.5 h-3.5 accent-[#3062D4] cursor-pointer"
                                                />
                                                <span className="text-xs font-interRegular text-[#4F4D55]">
                                                    Include in search
                                                </span>
                                            </label>
                                        </div>
                                        <textarea
                                            value={patentQuery}
                                            onChange={(e) => setPatentQuery(e.target.value)}
                                            disabled={!usePatentQuery}
                                            className={`w-full border rounded-lg px-3 py-2.5 text-sm font-interRegular text-[#0A090B] resize-none focus:outline-none focus:border-[#3062D4] focus:ring-1 focus:ring-[#3062D4] transition-opacity ${
                                                usePatentQuery
                                                    ? "border-[#E6E6E6]"
                                                    : "border-[#E6E6E6] opacity-40 bg-[#F9FAFB] cursor-not-allowed"
                                            }`}
                                            rows={3}
                                            placeholder="Patent search query..."
                                        />
                                    </div>

                                    {/* Prior Art Query */}
                                    <div className="mb-4">
                                        <div className="flex items-center justify-between mb-1.5">
                                            <label className="text-sm font-interMedium text-[#2D2B32]">
                                                Prior Art Query
                                            </label>
                                            <label className="flex items-center gap-x-1.5 cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={usePriorArtQuery}
                                                    onChange={(e) => setUsePriorArtQuery(e.target.checked)}
                                                    className="w-3.5 h-3.5 accent-[#3062D4] cursor-pointer"
                                                />
                                                <span className="text-xs font-interRegular text-[#4F4D55]">
                                                    Include in search
                                                </span>
                                            </label>
                                        </div>
                                        <textarea
                                            value={priorArtQuery}
                                            onChange={(e) => setPriorArtQuery(e.target.value)}
                                            disabled={!usePriorArtQuery}
                                            className={`w-full border rounded-lg px-3 py-2.5 text-sm font-interRegular text-[#0A090B] resize-none focus:outline-none focus:border-[#3062D4] focus:ring-1 focus:ring-[#3062D4] transition-opacity ${
                                                usePriorArtQuery
                                                    ? "border-[#E6E6E6]"
                                                    : "border-[#E6E6E6] opacity-40 bg-[#F9FAFB] cursor-not-allowed"
                                            }`}
                                            rows={3}
                                            placeholder="Prior art search query..."
                                        />
                                    </div>

                                    {/* Number of Results */}
                                    <div>
                                        <label className="text-sm font-interMedium text-[#2D2B32] block mb-1.5">
                                            Number of Results
                                        </label>
                                        <select
                                            value={numResults}
                                            onChange={(e) => setNumResults(Number(e.target.value))}
                                            className="border border-[#E6E6E6] rounded-lg px-3 py-2.5 text-sm font-interRegular text-[#0A090B] focus:outline-none focus:border-[#3062D4] focus:ring-1 focus:ring-[#3062D4]"
                                        >
                                            <option value={5}>5</option>
                                            <option value={10}>10</option>
                                            <option value={15}>15</option>
                                            <option value={20}>20</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Right: Claim Elements */}
                                <div className="w-[300px] flex-shrink-0">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-sm font-interSemiBold text-[#0A090B]">
                                            Claim Elements
                                        </h3>
                                        <button
                                            onClick={handleSelectAllElements}
                                            className="text-xs text-[#3062D4] hover:text-blue-800 font-interMedium"
                                        >
                                            {selectedElements.length === allElements.length
                                                ? "Deselect All"
                                                : "Select All"}
                                        </button>
                                    </div>

                                    <div className="border border-[#E6E6E6] rounded-lg overflow-hidden">
                                        <div className="px-3 py-2 bg-[#F9FAFB] border-b border-[#E6E6E6]">
                                            <span className="text-xs font-interMedium text-[#4F4D55]">
                                                {selectedElements.length} of {allElements.length} selected
                                            </span>
                                        </div>
                                        <div className="max-h-[400px] overflow-y-auto">
                                            {allElements.length === 0 ? (
                                                <div className="px-3 py-6 text-center">
                                                    <p className="text-sm text-[#7F7D83]">
                                                        No claim elements found
                                                    </p>
                                                </div>
                                            ) : (
                                                allElements.map((element, index) => (
                                                    <div
                                                        key={index}
                                                        className="flex items-start gap-x-2.5 px-3 py-2.5 border-b border-[#F1F1F1] last:border-b-0 hover:bg-[#F9FAFB]"
                                                    >
                                                        <div className="flex-shrink-0 mt-0.5">
                                                            <CustomCheckBox
                                                                id={`element-${index}`}
                                                                name={`element-${index}`}
                                                                label=""
                                                                checked={selectedElements.includes(element)}
                                                                onChange={(_: string, checked: boolean) =>
                                                                    handleElementToggle(element, checked)
                                                                }
                                                                customStyle="flex-shrink-0"
                                                            />
                                                        </div>
                                                        <span className="text-sm font-interRegular text-[#0A090B] leading-[1.5]">
                                                            {element}
                                                        </span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {!isLoading && (
                        <div className="px-6 py-4 bg-white border-t border-[#E6E6E6]">
                            <div className="flex justify-between gap-4">
                                <button
                                    onClick={handleClose}
                                    className="flex-1 px-4 py-3 text-sm font-interMedium text-[#4F4D55] hover:bg-gray-50 rounded-md border border-[#E6E6E6]"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleRegenerateQueries}
                                    disabled={isRegenerating || selectedElements.length === 0}
                                    className={`flex-1 px-4 py-3 text-sm font-interMedium text-[#3062D4] border border-[#3062D4] rounded-md hover:bg-blue-50 ${
                                        isRegenerating || selectedElements.length === 0
                                            ? "opacity-50 cursor-not-allowed"
                                            : ""
                                    }`}
                                >
                                    {isRegenerating ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#3062D4]"></span>
                                            Regenerating...
                                        </span>
                                    ) : (
                                        "Regenerate Queries"
                                    )}
                                </button>
                                <button
                                    onClick={handleFindSimilarProducts}
                                    disabled={isFinding || (!useProductQuery && !usePatentQuery && !usePriorArtQuery)}
                                    className={`flex-1 px-4 py-3 text-sm font-interMedium text-white bg-[#3062D4] hover:bg-blue-700 rounded-md ${
                                        isFinding || (!useProductQuery && !usePatentQuery && !usePriorArtQuery)
                                            ? "opacity-50 cursor-not-allowed"
                                            : ""
                                    }`}
                                >
                                    {isFinding ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
                                            Searching...
                                        </span>
                                    ) : (
                                        "Find Similar Products"
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchQueriesModal;
